---
title: "Interview templates for scheduling"
description: "Define interview templates in Chosen with required and optional interviewers, so the AI scheduler knows who to book for each interview type."
category: "scheduling"
keywords:
  - "interview templates"
  - "interview stages"
  - "required interviewers"
  - "interview type setup"
schema: "HowTo"
nav:
  section: "guides"
  order: 60
  parent: "/guides/scheduling"
---

An interview template defines an interview type once — its name, and the interviewers who run it — so you don't rebuild it for every candidate. When you hit **Schedule**, you pick the template and the interviewers come pre-filled. The AI scheduler does the rest.

## What a template holds

A template is an interview type with its panel attached: a phone screen, a technical round, an onsite loop. For each one you set the interviewers, and you mark each interviewer as **required** or **optional**.

That required-versus-optional split is the whole point of the feature, and the next section is why.

## Required vs optional interviewers

The distinction tells the AI scheduler how hard to hold out for each person.

- **Required** interviewers must all be free. The scheduler waits for the first slot where every one of them is available before it books anything.
- **Optional** interviewers are included if they fit and skipped if they don't. A nice-to-have second engineer won't block the interview when their calendar is full.

So a technical round might require the hiring manager and the lead engineer, and list a second engineer as optional. The scheduler books around the two required people and adds the third only when a slot happens to suit them.

<Callout type="info" title="Mark optional what's actually optional">
Every required interviewer is one more calendar the scheduler must thread. The more required people, the fewer overlaps exist and the longer scheduling takes. If someone is genuinely nice-to-have, mark them optional — it widens the search and books interviews faster.
</Callout>

## How templates feed the scheduler

When you start a scheduling request from a candidate's detail page, pick the interview template. The required and optional interviewers populate from it — you can still adjust them for this one candidate before starting.

From there the [AI auto-scheduler](/guides/scheduling/ai-scheduler-overview) takes the panel, reads each interviewer's [availability window](/guides/scheduling/availability-windows) and calendar, and finds slots that work for everyone required. The template is the starting point; it doesn't lock you in.

## When it gets it wrong

- **The wrong people are pre-filled.** The template is out of date — update its interviewer list, or just adjust the panel for this candidate before starting the request.
- **Scheduling is slow for a routine round.** Too many interviewers are marked required. Move the nice-to-haves to optional and the scheduler has more room.
- **An optional interviewer never gets booked.** That's expected when their calendar is full — optional means skippable. Make them required if they truly must attend.

## Limits

- A template carries the interview type and its panel. Per-candidate timing preferences belong in [natural-language constraints](/guides/scheduling/nl-constraints), not the template.
- Changing a template doesn't touch interviews already scheduled. It applies to new scheduling requests.
